import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import type { InspectorResource } from '@/lib/apiTypes'
import { useStateResources, useWorkspaceProviders } from '@/lib/hooks'
import { parseProviderRef, providerRefKey, type ProviderRef } from '@/features/state/lib/tfAddress'
import { RegistryIcon } from '@/shared/components/RepoProviderIcon'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { formatCount } from '@/lib/format'
import { Tooltip } from '@/shared/components/Tooltip'

interface Props {
  orgId: string
  workspace: string
  versionId: string
}

interface ProviderUsage {
  key: string
  ref: ProviderRef
  /** Managed resources at this serial using this provider instance. */
  managed: number
  /** Data sources at this serial using this provider instance. */
  data: number
  /** True when every resource fell back to the type prefix (no `provider` recorded in state). */
  derived: boolean
}

/** Group a serial's resources by provider instance. Data sources are counted separately: they
 *  read rather than create, so folding them into one total overstates the footprint. */
function aggregate(resources: InspectorResource[]): ProviderUsage[] {
  const byKey = new Map<string, ProviderUsage>()
  for (const r of resources) {
    const ref = parseProviderRef(r.provider, r.type)
    if (!ref) continue
    const key = providerRefKey(ref)
    let row = byKey.get(key)
    if (!row) {
      row = { key, ref, managed: 0, data: 0, derived: true }
      byKey.set(key, row)
    }
    if (r.mode === 'data') row.data++
    else row.managed++
    // One addressed resource is enough to stop calling the whole row inferred.
    if (r.provider) row.derived = false
  }
  return [...byKey.values()].sort((a, b) => (b.managed + b.data) - (a.managed + a.data) || a.key.localeCompare(b.key))
}

/** `registry.opentofu.org` -> the OpenTofu mark; anything else gets the Terraform one. */
function upstreamOf(host: string): 'terraform.io' | 'opentofu.org' {
  return host.includes('opentofu') ? 'opentofu.org' : 'terraform.io'
}

/**
 * Which providers THIS serial actually uses, read from the state itself.
 *
 * Deliberately distinct from the workspace-level "Locked providers" tab. Terraform writes the
 * provider ADDRESS into state but never the version it resolved to - versions live only in
 * .terraform.lock.hcl, which is stored once per workspace and overwritten on every upload. So the
 * locked version shown here is the workspace's current lock entry, NOT what this serial was
 * applied with, and it is labelled as such rather than quietly implied.
 */
export function ProvidersInUseTab({ orgId, workspace, versionId }: Props) {
  // Its own fetch rather than sharing the Resource Inspector's: that component owns its
  // `compareMode` state, which changes the fetch args. Only one tab is mounted at a time, so this
  // costs one request on tab switch, not a duplicate in flight.
  const resources = useStateResources(orgId, workspace, versionId, null)
  const locked = useWorkspaceProviders(orgId, workspace)

  const rows = useMemo(() => aggregate(resources.data?.resources ?? []), [resources.data])

  // Locked versions keyed the way the state side names providers, so the join is namespace/type.
  // The upstream comes along because it is part of a provider's identity in the Providers page
  // URL, and the lock file's value is authoritative - the state address only carries a registry
  // host, and a provider can be listed once per registry.
  const lockedByKey = useMemo(() => {
    const m = new Map<string, { version: string; upstream: string }>()
    for (const p of locked.data?.providers ?? []) {
      m.set(`${p.namespace}/${p.type}`, { version: p.current_version, upstream: p.upstream })
    }
    return m
  }, [locked.data])

  if (resources.loading) {
    return <SkeletonTable headers={['Provider', 'Alias', 'Resources', 'Data sources', 'Locked version']} rows={4} />
  }
  if (resources.error) {
    return (
      <div className="feedback-error">
        <p style={{ fontSize: '0.8rem' }}>Could not load this serial's resources: {resources.error}</p>
      </div>
    )
  }
  if (rows.length === 0) {
    return <EmptyState icon="▲" title="No providers recorded in this state version." />
  }

  return (
    <div className="fu">
      <p style={{ fontSize: '0.78rem', color: 'var(--text3)', marginBottom: 16, lineHeight: 1.5 }}>
        The providers this serial's state actually references, with the resources attributed to each.
        Terraform records the provider address in state but <strong>not</strong> the version it used,
        so the locked version below is the workspace's current lock file entry — not necessarily what
        this serial was applied with.
      </p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>
                <Tooltip text="Provider address recorded in this state version. Inferred means it was derived from the resource type, not recorded." placement="top">
                  <span tabIndex={0} className="cursor-help">Provider</span>
                </Tooltip>
              </th>
              <th>
                <Tooltip text="Provider alias from the configuration, where this instance is an aliased one." placement="top">
                  <span tabIndex={0} className="cursor-help">Alias</span>
                </Tooltip>
              </th>
              <th>
                <Tooltip text="Managed resources in this serial attributed to this provider." placement="top">
                  <span tabIndex={0} className="cursor-help">Resources</span>
                </Tooltip>
              </th>
              <th>
                <Tooltip text="Data sources in this serial attributed to this provider." placement="top">
                  <span tabIndex={0} className="cursor-help">Data sources</span>
                </Tooltip>
              </th>
              <th>
                <Tooltip text="The workspace's current lock file entry - not necessarily the version this serial was applied with." placement="top">
                  <span tabIndex={0} className="cursor-help">Locked version</span>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const lockedEntry = lockedByKey.get(`${row.ref.namespace}/${row.ref.type}`)
              // Only a provider the lock file recorded is known to exist as an artefact on the
              // Providers page - that page's rows come from provider_artefacts, which is populated
              // from lock-file uploads. A provider seen only in state has no artefact to open, so
              // it stays plain text rather than becoming a link to a 404.
              const href = lockedEntry
                ? `/org/${orgId}/providers/${encodeURIComponent(lockedEntry.upstream)}/${encodeURIComponent(row.ref.namespace)}/${encodeURIComponent(row.ref.type)}`
                : null
              const label = row.ref.namespace ? `${row.ref.namespace}/${row.ref.type}` : row.ref.type
              return (
                <tr key={row.key}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'inline-flex', color: 'var(--text2)' }}>
                        <RegistryIcon upstream={upstreamOf(row.ref.host)} size={16} />
                      </span>
                      {href ? (
                        <Link
                          to={href}
                          style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--accent)', textDecoration: 'none' }}
                          title={`Open ${label} in Providers`}
                        >
                          {label}
                        </Link>
                      ) : (
                        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem' }}>{label}</span>
                      )}
                      {/* No address in state - the provider was inferred from the resource type
                          prefix, which is a guess worth marking rather than presenting as fact. */}
                      {row.derived && (
                        <span className="badge badge-neutral" style={{ fontSize: '0.58rem' }}>inferred</span>
                      )}
                    </div>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: '0.75rem', color: 'var(--text3)' }}>
                    {row.ref.alias ?? '-'}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem' }}>{formatCount(row.managed)}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--text3)' }}>
                    {row.data > 0 ? formatCount(row.data) : '-'}
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {lockedEntry && href ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {/* ?v= opens the artefact on its Versions tab with this version already
                            expanded - its CVEs, usage and build detail without hunting for the
                            row. The version is a query param because the Providers page keys its
                            path on the artefact, not the version. */}
                        <Link
                          to={`${href}?v=${encodeURIComponent(lockedEntry.version)}`}
                          style={{ fontFamily: 'var(--mono)', color: 'var(--accent)', textDecoration: 'none' }}
                          title={`Open ${label} ${lockedEntry.version} in Providers`}
                        >
                          {lockedEntry.version}
                        </Link>
                        <span
                          className="badge badge-neutral"
                          style={{ fontSize: '0.58rem' }}
                          title="From the workspace lock file, not from this serial"
                        >
                          workspace
                        </span>
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text3)', fontSize: '0.75rem' }}>
                        {locked.loading ? '…' : 'not in lock file'}
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
